import { Dialog, DialogPanel } from '@headlessui/react'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import { IoMdClose } from 'react-icons/io'
import * as Yup from 'yup'
import Loading from '../Loading'
import { ApiGet, ApiPost, ApiPut, getHttpOptions } from '../../helper/axiosFunctions'
import { commonToast } from '../../helper/Toast'
import { generateRandomId } from '../../helper/commonFunctions'
import { useSelector } from 'react-redux'
import { useCallback, useEffect, useRef, useState } from 'react'
import ImageSkeleton from '../skeleton/ImageSkeleton'
import axios from 'axios'

const REGION_OPTIONS = [
    { code: 'LT', name: 'Lithuania', flag: '🇱🇹' },
    { code: 'CO', name: 'Colombia', flag: '🇨🇴' },
    { code: 'ES', name: 'Spain', flag: '🇪🇸' },
    { code: 'EN', name: 'English', flag: '🇬🇧' },
]

// Deck language should follow the selected country, not a hardcoded "en".
const COUNTRY_TO_LANGUAGE = {
    LT: 'lt',
    CO: 'es',
    ES: 'es',
    EN: 'en',
}

export default function CreateRegularDeckModel({ open, setIsOpen, onSuccess, editMode = false, editingDeck = null }) {
    const { t } = useTranslation()
    const handleClose = () => {
        setIsOpen(false)
        setSelectedImage(null)
    }
    const user_data = useSelector((state) => state.profileData.profileData);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [file1, setFile1] = useState(null);
    const [file2, setFile2] = useState(null);
    const coverInputRef = useRef();

    const DEFAULT_DECK_IMAGE = 'https://zubrusstorage2.blob.core.windows.net/avatar-colour/DeckDefault.png';

    const validationSchema = Yup.object({
        deckName: Yup.string()
            .required(t('validation.deckNameRequired')),
        shortDescription: Yup.string(),
        longDescription: Yup.string(),
        mainSubject: Yup.string()
            .required(t('validation.mainSubjectRequired')),
        region: Yup.string()
            .required(t('validation.regionRequired')),
        status: Yup.string()
            .required(t('validation.statusRequired'))
    })

    // const getImages = useCallback(async () => {
    //     setLoading(true);
    //     try {
    //         const response = await ApiGet(`/contents/images`);
    //         setData(response?.data);
    //     } catch (error) {
    //         commonToast(error?.data?.errorDescription || error?.data?.error || error?.message, "error");
    //     } finally {
    //         setLoading(false);
    //     }
    // }, []);

    useEffect(() => {
        if (!open) {
            setFile1(null);
            setFile2(null)
        }
    }, [open]);



    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        // if (!selectedImage) {
        //     commonToast("Please select an image", "error");
        //     return;
        // }

        const deckId = editMode ? editingDeck.deckId : generateRandomId()


        const uploadUrls = file1 || file2 ? await uploadDeckImages(deckId) : null;
        console.log("uploadUrls", uploadUrls)
        
        // Determine folder value
        const folderValue = editMode && editingDeck?.folder ? editingDeck.folder : deckId;
        console.log("folderValue:", folderValue);

        // schoolGrades must be an array. The form field accepts a single string
        // (optionally comma-separated like "5, 6-8, 10-12"). Convert it into an
        // array of trimmed strings; fall back to [""] when empty to match the
        // backend's expected shape.
        const schoolGradesArray = (() => {
            const raw = values.schoolGrades
            if (Array.isArray(raw)) {
                const cleaned = raw.map(g => (g ?? '').toString().trim()).filter(Boolean)
                return cleaned.length > 0 ? cleaned : [""]
            }
            const str = (raw ?? '').toString().trim()
            if (!str) return [""]
            const parts = str.split(',').map(g => g.trim()).filter(Boolean)
            return parts.length > 0 ? parts : [""]
        })()

        const payload = {
            deckId: deckId,
            authorId: user_data?.userId,
            status: String(values.status).toUpperCase(),
            deckName: values.deckName,
            shortDescription: values.shortDescription || values.deckName,
            longDescription: values.longDescription || values.deckName,
            mainSubject: values.mainSubject,
            smallImageUrl: editMode
                ? (file1 ? uploadUrls?.[0] : editingDeck.smallImageUrl)
                : (file1 ? uploadUrls?.[0] : DEFAULT_DECK_IMAGE),
            largeImageUrl: editMode
                ? (file2 ? uploadUrls?.[file1 ? 1 : 0] : editingDeck.largeImageUrl)
                : (file2 ? uploadUrls?.[1] : DEFAULT_DECK_IMAGE),
            language: COUNTRY_TO_LANGUAGE[values.region] || 'lt',
            countries: values.region ? [values.region] : ['LT'],
            "schoolGrades": schoolGradesArray,
            // Add folder field - same logic as image upload
            folder: folderValue
        }

        // Only include _id when editing (and we actually have a deckId).
        // Previously this used `editMode && editingDeck.deckId` as the value,
        // which serialized to `false` for create requests.
        if (editMode && editingDeck?.deckId) {
            payload._id = editingDeck.deckId
        }
        if (editMode) {
            await ApiPut(`/contents/decks`, payload)
                .then((res) => {
                    commonToast(t('deckUpdatedSuccess'), 'success')
                    setIsOpen(false)
                    onSuccess()
                    resetForm()
                    setSelectedImage(null)
                })
                .catch((error) => {
                    commonToast(error?.data?.errorDescription || error?.data?.error || error?.message, 'error')
                })
                .finally(() => {
                    setSubmitting(false)
                })
        } else {
            await ApiPost('/contents/decks', payload)
                .then((res) => {
                    commonToast(t('deckCreatedSuccess'), 'success')
                    setIsOpen(false)
                    onSuccess()
                    resetForm()
                    setSelectedImage(null)
                })
                .catch((error) => {
                    commonToast(error?.data?.errorDescription || error?.data?.error || error?.message, 'error')
                })
                .finally(() => {
                    setSubmitting(false)
                })
        }
    }

    const uploadDeckImages = async (deckId) => {
        try {
            const folderOrDeckId = editMode && editingDeck?.folder ? editingDeck.folder : deckId;
            
            const formData = new FormData();
            formData.append("deckId", folderOrDeckId);
            file1 && formData.append("file1", file1);
            file2 && formData.append("file2", file2);

            const res = await ApiPost('/contents/images-upload/deck', formData)
            console.log("RES", res)
            return res?.data;
        } catch (error) {
            console.error("Upload error:", error.response?.data || error.message);
            return null;
        }
    };

    return (<>
        <Dialog
            open={open}
            as="div"
            className="relative z-30 focus:outline-none"
            onClose={() => { }}
        >
            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4 bg-black/35 backdrop-blur-sm">
                    <DialogPanel
                        transition
                        className="w-full relative max-w-md !rounded-3xl bg-white duration-300 ease-out data-[closed]:transform-[scale(95%)] data-[closed]:opacity-0"
                    >
                        <div className="p-6 relative z-10">
                            <div className="mb-5">
                                <h1 className='text-xl font-semibold'>{editMode ? t('editDeck') : t('creatdeck')}</h1>
                                <IoMdClose
                                    className="absolute right-5 top-7 cursor-pointer"
                                    fontSize={24}
                                    onClick={handleClose}
                                />
                            </div>
                            <div>
                                <Formik
                                    initialValues={{
                                        deckName: editMode ? editingDeck?.deckName : '',
                                        shortDescription: editMode ? editingDeck?.shortDescription : '',
                                        longDescription: editMode ? editingDeck?.longDescription : '',
                                        mainSubject: editMode ? editingDeck?.mainSubject : '',
                                        schoolGrades: editMode
                                            ? (Array.isArray(editingDeck?.schoolGrades)
                                                ? editingDeck.schoolGrades.filter(g => g && g.trim() !== '').join(', ')
                                                : (editingDeck?.schoolGrades || ''))
                                            : '',
                                        region: editMode ? (editingDeck?.countries?.[0] || '') : '',
                                        status: editMode ? editingDeck?.status : 'PRIVATE'
                                    }}
                                    validationSchema={validationSchema}
                                    onSubmit={handleSubmit}
                                    enableReinitialize
                                >
                                    {({ isSubmitting }) => (
                                        <Form>
                                            <div className="mb-3">
                                                <Field
                                                    type="text"
                                                    name="deckName"
                                                    placeholder={t("deckName")}
                                                    className="border h-[56px] w-full z-20 rounded-xl bg-transparent px-4 py-2 shadow-inner outline-none placeholder:text-gray-500 text-black tracking-widest"
                                                />
                                                <ErrorMessage
                                                    name="deckName"
                                                    component="div"
                                                    className="text-red-500 text-sm mt-1"
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <Field
                                                    as="textarea"
                                                    name="shortDescription"
                                                    placeholder={t("shortDescription")}
                                                    rows={3}
                                                    className="border w-full z-20 rounded-xl bg-transparent px-4 py-2 shadow-inner outline-none placeholder:text-gray-500 text-black tracking-widest"
                                                />
                                                <ErrorMessage
                                                    name="shortDescription"
                                                    component="div"
                                                    className="text-red-500 text-sm mt-1"
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <Field
                                                    as="textarea"
                                                    name="longDescription"
                                                    placeholder={t("longDescription")}
                                                    rows={4}
                                                    className="border w-full z-20 rounded-xl bg-transparent px-4 py-2 shadow-inner outline-none placeholder:text-gray-500 text-black tracking-widest"
                                                />
                                                <ErrorMessage
                                                    name="longDescription"
                                                    component="div"
                                                    className="text-red-500 text-sm mt-1"
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <Field
                                                    type="text"
                                                    name="mainSubject"
                                                    placeholder={t("subject")}
                                                    className="border h-[56px] w-full z-20 rounded-xl bg-transparent px-4 py-2 shadow-inner outline-none placeholder:text-gray-500 text-black tracking-widest"
                                                />
                                                <ErrorMessage
                                                    name="mainSubject"
                                                    component="div"
                                                    className="text-red-500 text-sm mt-1"
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <Field
                                                    type="text"
                                                    name="schoolGrades"
                                                    placeholder={t("schoolGrades")}
                                                    className="border h-[56px] w-full z-20 rounded-xl bg-transparent px-4 py-2 shadow-inner outline-none placeholder:text-gray-500 text-black tracking-widest"
                                                />
                                                <p className="text-xs text-gray-400 mt-1 ml-1">{t("schoolGradesHelperText")}</p>
                                            </div>
                                            <div className="mb-3">
                                                <Field
                                                    as="select"
                                                    name="region"
                                                    className="border h-[56px] w-full z-20 rounded-xl bg-transparent px-4 py-2 shadow-inner outline-none text-black tracking-widest"
                                                >
                                                    <option value="" hidden>{t('selectRegion')}</option>
                                                    {REGION_OPTIONS.map((r) => (
                                                        <option key={r.code} value={r.code}>
                                                            {r.flag} {r.name}
                                                        </option>
                                                    ))}
                                                </Field>
                                                <ErrorMessage
                                                    name="region"
                                                    component="div"
                                                    className="text-red-500 text-sm mt-1"
                                                />
                                            </div>

                                            {/* <span className="regular-text text-gray-700 block mb-2">small image</span>
                                            
                                            {/* <div className="grid grid-cols-6 lg:grid-cols-4 gap-2 mt-2 min-h-[120px] mb-3">
                                                {loading ? (
                                                    <ImageSkeleton />
                                                ) : (
                                                    data?.map((item) => (
                                                        <div
                                                            key={item?.id}
                                                            onClick={() => setSelectedImage(item)}
                                                            className={`rounded-xl flex items-center justify-center p-2 cursor-pointer transition-all ${(selectedImage?.url) === item?.url
                                                                    ? 'border border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                                                                    : 'border border-gray-200 hover:bg-gray-50'
                                                                }`}
                                                        >
                                                            <img
                                                                src={item?.url}
                                                                loading="lazy"
                                                                alt={item?.imageName}
                                                                className="w-[47px] h-[45px] object-contain object-center"
                                                            />
                                                        </div>
                                                    ))
                                                )}
                                            </div> */}


                                            {/* <span className="regular-text text-gray-700 block mb-2">Picture</span> */}

                                            <div className="flex flex-col items-center my-4 justify-center gap-5">
                                                {/* Cover Image Box */}
                                                <div className="w-full">
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        {t('deckCover') || 'Cover'}
                                                    </label>
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        ref={coverInputRef}
                                                        className="hidden"
                                                        onChange={(e) => {
                                                            const file = e.target.files[0] || null;
                                                            setFile1(file);
                                                            setFile2(file);
                                                        }}
                                                    />
                                                    <div
                                                        onClick={() => coverInputRef.current.click()}
                                                        className="w-full max-w-[400px] h-[150px] border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer bg-cover bg-center hover:border-blue-400 hover:bg-blue-50 transition-all duration-200"
                                                        style={{
                                                            backgroundImage: file1
                                                                ? `url(${URL.createObjectURL(file1)})`
                                                                : editMode && editingDeck?.largeImageUrl
                                                                    ? `url(${editingDeck.largeImageUrl})`
                                                                    : "none",
                                                        }}
                                                    >
                                                        {!file1 && !(editMode && editingDeck?.largeImageUrl) ? (
                                                            <div className="text-center">
                                                                <svg className="mx-auto h-8 w-8 text-gray-400 mb-2" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                                                                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                                                                </svg>
                                                                <span className="text-gray-500 text-sm">{t('clickToUploadCoverImage') || t('clickToUploadLargeImage')}</span>
                                                            </div>
                                                        ) : (
                                                            <div className="relative w-full h-full group">
                                                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded-lg flex items-center justify-center">
                                                                    <span className="text-white opacity-0 group-hover:opacity-100 text-sm font-medium">{t('clickToChange')}</span>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>


                                            <div className="mb-3">
                                                <Field
                                                    as="select"
                                                    name="status"
                                                    className="border h-[56px] w-full z-20 rounded-xl bg-transparent px-4 py-2 shadow-inner outline-none text-black tracking-widest"
                                                >
                                                    <option value="" hidden>{t('selectStatus')}</option>
                                                    <option value="PUBLISHED">{t('Published')}</option>
                                                    <option value="HIDDEN">{t('Hidden')}</option>
                                                    <option value="PRIVATE">{t('Private')}</option>
                                                    <option value="WORK_IN_PROGRESS">{t('Work In Progress')}</option>
                                                    <option value="DELETED">{t('Deleted')}</option>
                                                </Field>
                                                <ErrorMessage
                                                    name="status"
                                                    component="div"
                                                    className="text-red-500 text-sm mt-1"
                                                />
                                            </div>

                                            <div className="flex gap-3">
                                                <button
                                                    type="submit"
                                                    className="primary-btn flex-1"
                                                    disabled={isSubmitting}
                                                >
                                                    {isSubmitting ? <Loading /> : t('save')}
                                                </button>
                                            </div>
                                        </Form>
                                    )}
                                </Formik>
                            </div>
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    </>)
}
